import { useEffect, useState } from 'react';

import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { IRole, IType } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import {
    MATRIX_COMPONENTS,
    PRIORITIES_MATRIX_FILTERS,
    getLinkToBattlecards,
    initialMatrixFilter,
} from 'components/widgets/PrioritiesMatrix/prioritiesMatrix.utils';

export const usePrioritiesMatrixLogic = ({ widgetId, ui, api }) => {
    const { email } = useUserHook();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const [filtersTitle, setFiltersTitle] = useState<string>('Urgent');
    const [filteredCardsData, setFilteredCardsData] = useState(api.cards.data);

    const urgentFilter = PRIORITIES_MATRIX_FILTERS.find(
        (filter) => filter.value === String(ui.state.numOfDaysToBeUrgent),
    );

    const setFilterTitle = (componentType: string) => {
        const title = MATRIX_COMPONENTS[componentType].urgent ? 'Urgent' : ' Not Urgent';
        setFiltersTitle(title);
    };

    const handleFilterClick = (filter: ISelectOption<string>) => {
        const value = filter.value;
        sendEventWithDimensions({
            category: 'Priorities Matrix',
            action: 'Range Selection',
            label: value,
        });
        ui.setters.setMatrixFilterUrlParam(value);
    };

    const fetchCardsData = ({ itemId }) => {
        ui.setters.setMatrixCardCategoryId(itemId);
    };

    const getAssignedToMeQueryFilter = () => {
        if (ui.state.isAssignedToMeFilter) {
            const assignedId = api.cards?.data?.find((item) => item.assignee.userName === email);
            return assignedId ? assignedId?.assignee.userName : null;
        }

        return null;
    };

    const linkToProjectActivities = getLinkToBattlecards({
        ui,
        getAssignedToMeQueryFilter,
    });

    const handleMatrixButtonClick = ({ type, role, itemId }: { type: IType; role: IRole; itemId: number }) => {
        // reset pills filters
        ui.setters.setIsOverdueFilter(false);
        ui.setters.setIsDelayDriverFilter(false);
        ui.setters.setIsCriticalPathFilter(false);
        ui.setters.setIsAssignedToMeFilter(false);
        // ======================================

        const label = api.matrixData.instance.data
            ? `${api.matrixData.instance.data[type].title} ${api.matrixData.instance.data[type][role].title}`
            : '';
        sendEventWithDimensions({
            category: 'Priorities Matrix',
            action: 'Cell Selection',
            label,
        });
        ui.setters.setSelectedTypeAndRole({ type, role });
        setFilterTitle(type);
        fetchCardsData({ itemId });
    };

    const selectedTypeWithRole =
        api.matrixData.instance.data?.[ui.state.selectedTypeAndRole.type][ui.state.selectedTypeAndRole.role];

    const handlePillsFilter = () => {
        const filtersObj = {
            isOverdue: ui.state.isOverdueFilter,
            isDelayDriver: ui.state.isDelayDriverFilter,
            isCriticalPath: ui.state.isCriticalPathFilter,
        };

        const keys = Object.keys(filtersObj).filter((x) => filtersObj[x]);

        const filteredCards = api.cards.data?.filter((item) => {
            return ui.state.isAssignedToMeFilter
                ? item.assignee.userName === email && keys.every((k) => item?.cardIndicators[k]?.isIndicatorOn)
                : keys.every((k) => item?.cardIndicators[k]?.isIndicatorOn);
        });

        setFilteredCardsData(filteredCards);
    };

    useEffect(() => {
        if (!ui.state.matrixFilterUrlParam && !widgetId) {
            ui.setters.setMatrixFilterUrlParam(initialMatrixFilter);
        }
    }, [ui.state.matrixFilterUrlParam, widgetId]);

    useEffect(() => {
        if (api.cards.data) {
            handlePillsFilter();
        }
    }, [
        api.cards.data,
        ui.state.isOverdueFilter,
        ui.state.isDelayDriverFilter,
        ui.state.isCriticalPathFilter,
        ui.state.isAssignedToMeFilter,
    ]);

    return {
        logic: {
            setFilterTitle,
            filtersTitle,
            handleFilterClick,
            urgentFilter,
            linkToProjectActivities,
            fetchCardsData,
            handleMatrixButtonClick,
            selectedTypeWithRole,
            filteredCardsData,
        },
    };
};
